import { useState } from 'react';
import { ChevronDown, ChevronUp, ShieldAlert } from 'lucide-react';
import AppNav from './AppNav';
import styles from './AppHeader.module.css';

export default function AppHeader({
  current,
  onGoToSessions,
  contextTools = null,
  showBrand = true,
  collapsible = false,
  collapsed = false,
  onToggleCollapsed,
  collapsedMeta = null,
  currentUser,
  onStopMasquerade,
  ...navProps
}) {
  const goHome = onGoToSessions || (() => { window.location.href = '/'; });
  const [stopError, setStopError] = useState('');
  const [stopping, setStopping] = useState(false);
  const masquerade = currentUser?.masquerade;
  const expiresLabel = masquerade?.expires_at
    ? new Date(masquerade.expires_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null;
  const banner = masquerade ? (
    <div className={styles.masqueradeBanner} role="status">
      <ShieldAlert size={16} aria-hidden="true" />
      <span>
        Masquerading as <strong>{currentUser.display_name || currentUser.email}</strong>
        {currentUser.display_name ? ` (${currentUser.email})` : ''}
        {expiresLabel ? ` · expires ${expiresLabel}` : ''}
      </span>
      {stopError && <span className={styles.masqueradeError}>{stopError}</span>}
      <button disabled={stopping} onClick={async () => {
        setStopping(true);
        setStopError('');
        try { await onStopMasquerade?.(); }
        catch (error) { setStopError(error?.message || 'Could not stop'); setStopping(false); }
      }}>
        {stopping ? 'Stopping…' : 'Stop masquerading'}
      </button>
    </div>
  ) : null;

  if (collapsible && collapsed) {
    return (
      <>
        {banner}
        <header className={styles.collapsedHeader}>
          <AppNav
            shell compact current={current} onGoToSessions={onGoToSessions}
            contextTools={contextTools} compactMeta={collapsedMeta}
            currentUser={currentUser} {...navProps}
          />
          <button
            className={styles.collapsedToggle}
            onClick={onToggleCollapsed}
            aria-label="Expand analysis menu"
            aria-expanded="false"
            title="Expand analysis menu"
          >
            <ChevronDown size={15} aria-hidden="true" />
          </button>
        </header>
      </>
    );
  }

  return (
    <>
      {banner}
      <header className={styles.header}>
        {showBrand && (
          <button
            className={styles.brand}
            onClick={goHome}
            title={onGoToSessions ? 'Go to Sessions' : 'Track Day Analysis home'}
            aria-label={onGoToSessions ? 'Track Day Analysis — go to Sessions' : 'Track Day Analysis home'}
          >
            <img src="/TDA_Logo.jpg" alt="" />
            <span>Track Day Analysis</span>
          </button>
        )}
        <AppNav
          shell current={current} onGoToSessions={onGoToSessions}
          contextTools={contextTools} currentUser={currentUser} {...navProps}
        />
        {collapsible && (
          <button
            className={styles.collapseBtn}
            onClick={onToggleCollapsed}
            aria-label="Collapse analysis menu"
            aria-expanded="true"
            title="Collapse analysis menu"
          >
            <ChevronUp size={16} aria-hidden="true" />
          </button>
        )}
      </header>
    </>
  );
}
